import { useCallback, useEffect, useRef, useState } from 'react';
import { VendorTrialCard } from '../../../modules/superadmin/pricing/VendorTrialCard';
import { VendorContractCard } from '../../../modules/superadmin/pricing/VendorContractCard';
import { VendorOfferCard } from '../../../modules/superadmin/pricing/VendorOfferCard';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ExternalLink, UserRoundCog } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AdminPage, EmptyState, SaveBar } from '../../../components/design-system/admin';
import { Vendor, VendorPlan } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import { VendorGeneralInformation } from '../../../modules/superadmin/edit-vendor/components/vendor-general-information.form';
import { VendorPlanBilling } from '../../../modules/superadmin/edit-vendor/components/vendor-plan-billing.form';
import { VendorInvoicesPanel } from '../../../modules/superadmin/edit-vendor/components/vendor-invoices.panel';
import { VendorLogoCard } from '../../../modules/superadmin/edit-vendor/components/vendor-logo.card';
import { VendorSpaces } from '../../../modules/superadmin/edit-vendor/components/vendor-spaces.form';
import { VendorFamilies } from '../../../modules/superadmin/edit-vendor/components/vendor-families.form';
import { VendorConfig } from '../../../modules/superadmin/edit-vendor/components/vendor-config.form';
import { VendorCreditsPanel } from '../../../modules/superadmin/edit-vendor/components/vendor-credits.form';
import { VendorDevicesPanel } from '../../../modules/superadmin/edit-vendor/components/vendor-devices-panel';
import { VendorOverviewPanel } from '../../../modules/superadmin/edit-vendor/components/vendor-overview.panel';
import { AdminStoreKnowledgeCard } from '../../../modules/store-knowledge/AdminStoreKnowledgeCard';
import { VendorPageHeader } from '../../../modules/superadmin/edit-vendor/components/vendor-page-header';
import { getDiff } from '../../../common/utils/utils';
import { setLoading } from '../../../redux/vendor/ui/uiActions';
import { useVendorsApi } from '../../../common/hooks/use-vendors-api.hook';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import logger from '../../../utils/logger';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../common/utils/visualizer-link';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { SupportSession, useSupportSessionsApi } from '../../../common/hooks/use-support-sessions-api.hook';
import { OpenAsVendorDialog } from '../../../modules/support-access/OpenAsVendorDialog';
import { SupportSessionFrame } from '../../../modules/support-access/SupportSessionFrame';
import { SupportSessionsHistory } from '../../../modules/support-access/SupportSessionsHistory';
import { VendorUsagePanel } from '../../../modules/superadmin/vendor-usage/VendorUsagePanel';
import { VendorTimelinePanel } from '../../../modules/superadmin/vendor-usage/VendorTimelinePanel';

export type VendorTab = 'overview' | 'profile' | 'plan' | 'branding' | 'catalog' | 'devices' | 'support' | 'usage' | 'activity';

const EDIT_TABS: VendorTab[] = ['overview', 'usage', 'activity', 'profile', 'plan', 'branding', 'catalog', 'devices', 'support'];
const READ_ONLY_TABS: VendorTab[] = ['overview', 'usage', 'activity', 'devices', 'support'];
// A vendor that does not exist yet has no numbers and no devices.
const CREATE_TABS: VendorTab[] = ['profile', 'plan', 'branding', 'catalog'];

// Where a new vendor starts. The colours are the vendor's brand (data), not UI chrome.
const createDefaults = (): Vendor => ({
    maxSpaces: 3,
    maxProducts: 50,
    config: {
        fontFamily: 'Lexend',
        language: ['fr'],
        secondaryColor: '#217ee8',
        primaryColor: '#217ee8',
        // The best render by default; the Branding tab can lower it (ARDE-275).
        visualizationQuality: 'high',
    },
});

/** What Save sends: a blank or zero ceiling falls back to the default. */
const withNumericDefaults = (vendor: Vendor): Vendor => ({
    ...vendor,
    maxSpaces: (!vendor.maxSpaces || vendor.maxSpaces === 0) ? 3 : Number(vendor.maxSpaces),
    maxProducts: (!vendor.maxProducts || vendor.maxProducts === 0) ? 50 : Number(vendor.maxProducts),
});

/**
 * The tabs holding a field Save would refuse, in tab order — mirrors the
 * validation in handleSave. Drives the dots on the tabs and where a refused
 * save takes you.
 */
const tabsWithProblems = (v: Vendor, isCreate: boolean): VendorTab[] => {
    const out: VendorTab[] = [];
    const profileMissing = !v.name || !v.alias || !v.email || !v.storeUrl || !v.areas || v.areas.length === 0;
    if (profileMissing || (isCreate && !v.config?.logo)) out.push('profile');
    if (isCreate && (!v.spaces || v.spaces.length === 0)) out.push('catalog');
    return out;
};

/**
 * A vendor's page (`/vendors/:vendorId/edit`) and the new-vendor form
 * (`/vendors/new`): one vendor object edited across tabs, saved by one Save.
 */
export const EditVendor = () => {
    const { t } = useTranslation();
    const [vendor, setVendor] = useState<Vendor>({});
    const [initialVendor, setInitialVendor] = useState<Vendor>({});
    const [submitted, setSubmitted] = useState<boolean>(false);
    const { vendorId } = useParams();
    const isCreate = !vendorId;
    const [loadingVendor, setLoadingVendor] = useState<boolean>(!!vendorId);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [plans, setPlans] = useState<VendorPlan[]>([]);
    const [creditsVersion, setCreditsVersion] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const vendorsAPI = useVendorsApi();
    const plansApi = usePlansApi();
    const supportApi = useSupportSessionsApi();
    const { user } = useAuthContext();
    const [openAsVendor, setOpenAsVendor] = useState(false);
    // Bumped when a support session starts or ends, so the Support tab reloads.
    const [supportVersion, setSupportVersion] = useState(0);
    // The support session shown in a frame over this page.
    const [supportSession, setSupportSession] = useState<SupportSession | null>(null);
    const closeSupportSession = useCallback(() => {
        setSupportSession(null);
        setSupportVersion((v) => v + 1);
    }, []);
    // API hooks return new objects every render; the loaders must not follow them.
    const vendorsApiRef = useRef(vendorsAPI);
    vendorsApiRef.current = vendorsAPI;

    const loadVendor = async (id: string) => {
        setLoadingVendor(true);
        setLoadFailed(false);
        try {
            const found = await vendorsApiRef.current.findOneVendor(id);
            // Ensure numeric fields have default values
            const normalizedVendor = {
                ...found,
                maxSpaces: found.maxSpaces ?? 3,
                maxProducts: found.maxProducts ?? 50,
            };
            setVendor(normalizedVendor);
            setInitialVendor(normalizedVendor);
        } catch (error) {
            logger.log(error);
            setLoadFailed(true);
        } finally {
            setLoadingVendor(false);
        }
    };

    useEffect(() => {
        setSubmitted(false);
        if (vendorId) {
            loadVendor(vendorId);
        } else {
            const defaults = createDefaults();
            setVendor(defaults);
            setInitialVendor(defaults);
            setLoadingVendor(false);
            setLoadFailed(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    useEffect(() => {
        plansApi.getPlans().then((data) => setPlans((data as VendorPlan[]) || [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Tabs: kept in the URL (?tab=) so a reload stays put ──────────────────
    const tabs = isCreate ? CREATE_TABS : EDIT_TABS;
    const requested = searchParams.get('tab') as VendorTab | null;
    const tab: VendorTab = requested && tabs.includes(requested) ? requested : tabs[0];
    const changeTab = (next: VendorTab) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', next);
        setSearchParams(params, { replace: true });
    };
    // A tab mounts on first visit and then stays mounted (hidden), so its edits,
    // open panels and fetched lists survive switching away and back.
    const visited = useRef<{ vendorId?: string; tabs: Set<VendorTab> }>({ vendorId, tabs: new Set() });
    if (visited.current.vendorId !== vendorId) visited.current = { vendorId, tabs: new Set() };
    visited.current.tabs.add(tab);

    const problems = submitted ? tabsWithProblems(withNumericDefaults(vendor), isCreate) : [];
    const dirty = !loadingVendor && !loadFailed && Object.keys(getDiff(initialVendor, vendor)).length > 0;

    const handleSave = async () => {
        setSubmitted(true);

        // Prepare vendor data with defaults for numeric fields
        const vendorToSave = withNumericDefaults(vendor);

        // Always-required fields (both create and edit). On edit, missing
        // logo/spaces is tolerated so general-info-only changes can still
        // save — Save would otherwise silently no-op for legacy vendors.
        const baseValid =
            vendorToSave.name &&
            vendorToSave.alias &&
            vendorToSave.email &&
            vendorToSave.storeUrl &&
            vendorToSave.areas && vendorToSave.areas.length > 0;
        const createOnlyValid =
            vendorToSave.config?.logo &&
            vendorToSave.spaces && vendorToSave.spaces.length > 0;

        if (!baseValid || (!vendorId && !createOnlyValid)) {
            toast.error(t('SUPER_ADMIN.EDIT_VENDOR.VALIDATION_FAILED'));
            // Take the admin to what is missing when it is on another tab.
            const missing = tabsWithProblems(vendorToSave, !vendorId);
            if (missing.length > 0 && !missing.includes(tab)) changeTab(missing[0]);
            return;
        }

        dispatch(setLoading(true));
        setSaving(true);
        try {
            if (!vendorId) {
                const newVendor = await vendorsAPI.createVendor(vendorToSave);
                toast.success(t('SUPER_ADMIN.EDIT_VENDOR.VENDOR_CREATED'));
                setVendor(newVendor);
                setInitialVendor(newVendor);
                navigate(`/vendors/${newVendor._id}/edit`, { replace: true });
            } else {
                const diff: any = getDiff(initialVendor, vendorToSave);
                // A cleared planOverrides comes through as undefined; JSON.stringify
                // would drop it, so the backend would never clear it. Send null.
                if ('planOverrides' in diff && diff.planOverrides == null) {
                    diff.planOverrides = null;
                }
                if (Object.keys(diff).length > 0) {
                    await vendorsAPI.updateVendor(vendorToSave._id, diff);
                    toast.success(t('SUPER_ADMIN.EDIT_VENDOR.VENDOR_UPDATED'));
                    setVendor(vendorToSave);
                    setInitialVendor(vendorToSave);
                } else {
                    toast(t('SUPER_ADMIN.EDIT_VENDOR.NO_CHANGES'));
                }
            }

            dispatch(setLoading(false));
            setSubmitted(false);
        } catch (error: any) {
            logger.log(error);
            dispatch(setLoading(false));
            const serverMessage = error?.message && !error.message.startsWith('Request failed')
                ? error.message
                : t('SUPER_ADMIN.EDIT_VENDOR.SAVE_FAILED');
            toast.error(serverMessage);
        } finally {
            setSaving(false);
        }
    };

    // ── Header ───────────────────────────────────────────────────────────────
    // The header shows the vendor as saved; the save bar says what is pending.
    // undefined = not known yet (plans still loading), null = no plan.
    const savedPlan: any = (initialVendor as any)?.plan;
    const savedPlanId = savedPlan?._id || savedPlan;
    const plansLoaded = plans.length > 0;
    const planName: string | null | undefined = savedPlanId
        ? plans.find((p) => p._id === savedPlanId)?.name ?? savedPlan?.name ?? (plansLoaded ? null : undefined)
        // No explicit plan → the backend applies the default plan.
        : plansLoaded ? plans.find((p) => p.isDefault)?.name ?? null : undefined;

    const openVisualizer = () =>
        window.open(buildVisualizerUrl({ alias: vendor.alias, attribution: PREVIEW_LINK_ATTRIBUTION }), '_blank', 'noopener,noreferrer');

    const header = isCreate ? undefined : (
        <VendorPageHeader
            vendor={initialVendor}
            planName={planName}
            loading={loadingVendor}
            actions={
                <>
                    {vendor?.alias && (
                        <Button variant="outline" onClick={openVisualizer} data-testid="vendor-open-visualizer">
                            <ExternalLink className="size-4" />
                            {t('ADMIN_SHELL.COMMON.OPEN_VISUALIZER')}
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => setOpenAsVendor(true)} data-testid="vendor-open-as-vendor">
                        <UserRoundCog className="size-4" />
                        {t('SUPPORT_ACCESS.OPEN')}
                    </Button>
                </>
            }
        />
    );

    // ── Tab content ──────────────────────────────────────────────────────────
    const showCreateRequired = submitted && isCreate;
    const content = (value: VendorTab) => {
        switch (value) {
            case 'usage':
                // How this vendor uses the backoffice: each feature, AI in $ and credits, quotas.
                return <VendorUsagePanel vendorId={vendorId} />;
            case 'activity':
                // What the vendor's team did in the backoffice: changes, sign-ins, pages, credits.
                return <VendorTimelinePanel vendorId={vendorId} />;
            case 'overview':
                return (
                    <div className="flex flex-col gap-4">
                        <VendorOverviewPanel vendorId={vendorId} creditsVersion={creditsVersion} />
                        {/* What the shopping assistant read on the vendor's own website. */}
                        <AdminStoreKnowledgeCard vendorId={vendorId} />
                    </div>
                );
            case 'profile':
                return (
                    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
                        <div className="lg:col-span-2">
                            <VendorGeneralInformation vendor={vendor} setVendor={setVendor} submitted={submitted} />
                        </div>
                        <VendorLogoCard
                            vendor={vendor}
                            setVendor={setVendor}
                            showRequired={showCreateRequired && !vendor.config?.logo}
                        />
                    </div>
                );
            case 'plan':
                return (
                    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
                        <div className={isCreate ? 'xl:col-span-3' : 'xl:col-span-2'}>
                            <VendorPlanBilling
                                vendor={vendor}
                                setVendor={setVendor}
                                submitted={submitted}
                                plans={plans}
                                onServerSaved={(patch) => setInitialVendor((prev) => ({ ...prev, ...patch }))}
                            />
                        </div>
                        {/* Trial (docs/pricing-v2.md): on the trial plan, or with a trial date. */}
                        {!isCreate && (() => {
                            const planId = (vendor as any)?.plan?._id || (vendor as any)?.plan;
                            const onTrialPlan = plans.find((p) => p._id === planId)?.pricingType === 'trial';
                            if (!onTrialPlan && !(vendor as any)?.trialEndsAt) return null;
                            return (
                                <div className="xl:col-span-1">
                                    <VendorTrialCard
                                        vendorId={(vendor as any)?._id || vendorId}
                                        trialEndsAt={(vendor as any)?.trialEndsAt}
                                        onChanged={(trialEndsAt) => {
                                            setVendor((prev: any) => ({ ...prev, trialEndsAt }))
                                            setInitialVendor((prev: any) => ({ ...prev, trialEndsAt }))
                                        }}
                                    />
                                </div>
                            );
                        })()}
                        {/* This vendor's own prices: what its plan page shows and checkout charges. */}
                        {!isCreate && (
                            <div className="xl:col-span-3">
                                <VendorOfferCard vendorId={(vendor as any)?._id || vendorId} plans={plans} />
                            </div>
                        )}
                        {/* The sales-led agreement: price, 12-month minimum term, renewal. */}
                        {!isCreate && (
                            <div className="xl:col-span-3">
                                <VendorContractCard vendorId={(vendor as any)?._id || vendorId} />
                            </div>
                        )}
                        {/* Invoices, and past ones uploaded as PDFs — existing vendors only. */}
                        {!isCreate && (
                            <div className="xl:col-span-3">
                                <VendorInvoicesPanel
                                    vendorId={(vendor as any)?._id || vendorId}
                                    defaultCurrency={plans.find((p) => p._id === ((vendor as any)?.plan?._id || (vendor as any)?.plan))?.currency}
                                />
                            </div>
                        )}
                        {/* Content Studio credits — existing vendors only (a new
                            vendor has no _id to hold a balance yet). */}
                        {!isCreate && (
                            <VendorCreditsPanel
                                vendorId={(vendor as any)?._id || vendorId}
                                onAdjusted={() => setCreditsVersion((n) => n + 1)}
                            />
                        )}
                    </div>
                );
            case 'branding':
                return <VendorConfig vendor={vendor} setVendor={setVendor} submitted={submitted} />;
            case 'catalog':
                return (
                    <div className="flex flex-col gap-4">
                        <VendorFamilies vendor={vendor} setVendor={setVendor} />
                        <VendorSpaces vendor={vendor} setVendor={setVendor} showRequired={showCreateRequired} />
                    </div>
                );
            case 'devices':
                // Seats in use and signed-in devices.
                return <VendorDevicesPanel vendorId={(vendor as any)?._id || vendorId} />;
            case 'support':
                // Support sessions in this account, and what each changed.
                return (
                    <SupportSessionsHistory
                        key={supportVersion}
                        audience="admin"
                        currentEmail={user?.email}
                        load={() => supportApi.listForVendor(vendorId)}
                        onResume={setSupportSession}
                        onEnd={async (s) => { await supportApi.end(s._id); }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <AdminPage
            title={isCreate ? t('ADMIN_VENDOREDIT.NEW_TITLE') : (initialVendor.name || t('ADMIN_VENDOREDIT.TITLE_FALLBACK'))}
            description={isCreate ? t('ADMIN_VENDOREDIT.NEW_DESCRIPTION') : undefined}
            parents={[{ label: t('ADMIN_SHELL.NAV.VENDORS'), href: '/vendors' }]}
            header={header}
        >
            {loadFailed ? (
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    action={
                        <Button variant="outline" onClick={() => loadVendor(vendorId)}>
                            {t('ADMIN_SHELL.COMMON.RETRY')}
                        </Button>
                    }
                    className="py-24"
                />
            ) : loadingVendor ? (
                <div className="flex flex-col gap-4" data-testid="vendor-loading">
                    <Skeleton className="h-8 w-full max-w-xl rounded-lg" />
                    <Skeleton className="h-96 rounded-xl" />
                </div>
            ) : (
                <>
                    <Tabs
                        key={vendorId ?? 'new'}
                        value={tab}
                        onValueChange={(next) => changeTab(next as VendorTab)}
                        className="gap-4"
                    >
                        <TabsList
                            aria-label={t('ADMIN_VENDOREDIT.TABS.LABEL')}
                            className="max-w-full justify-start overflow-x-auto"
                        >
                            {tabs.map((value) => (
                                <TabsTrigger key={value} value={value} className="flex-none px-3" data-testid={`vendor-tab-${value}`}>
                                    {t(`ADMIN_VENDOREDIT.TABS.${value.toUpperCase()}`)}
                                    {problems.includes(value) && (
                                        <span
                                            className="size-1.5 rounded-full bg-destructive"
                                            role="img"
                                            aria-label={t('ADMIN_VENDOREDIT.TABS.NEEDS_ATTENTION')}
                                            data-testid={`vendor-tab-${value}-problem`}
                                        />
                                    )}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {tabs.map((value) => (
                            <TabsContent key={value} value={value} forceMount hidden={value !== tab} data-testid={`vendor-panel-${value}`}>
                                {visited.current.tabs.has(value) ? content(value) : null}
                            </TabsContent>
                        ))}
                    </Tabs>

                    {/* Overview and Devices only read: no Save there, unless edits made elsewhere are waiting. */}
                    {(dirty || isCreate || !READ_ONLY_TABS.includes(tab)) && (
                        <SaveBar
                            dirty={dirty}
                            saving={saving}
                            onSave={handleSave}
                            saveLabel={isCreate ? t('ADMIN_VENDOREDIT.SAVE_BAR.CREATE') : t('ADMIN_VENDOREDIT.SAVE_BAR.SAVE')}
                            hint={isCreate && !dirty ? t('ADMIN_VENDOREDIT.SAVE_BAR.NOT_CREATED') : undefined}
                            onCancel={() => navigate('/vendors')}
                            testId="vendor-save"
                        />
                    )}
                </>
            )}
            {!isCreate && (
                <OpenAsVendorDialog
                    vendor={{ _id: vendorId, name: initialVendor.name }}
                    open={openAsVendor}
                    onOpenChange={setOpenAsVendor}
                    onStarted={(s) => {
                        setSupportSession(s);
                        setSupportVersion((v) => v + 1);
                    }}
                />
            )}
            {supportSession && <SupportSessionFrame session={supportSession} onClose={closeSupportSession} />}
        </AdminPage>
    );
};
